import { moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatLegacyDialog as MatDialog }from '@angular/material/legacy-dialog';
import { Subscription } from 'rxjs';
import { ContextChange, WidgetStyle } from '@sdbn/appclient';
import { ConfirmDialogComponent, ConfirmDialogData } from 'src/app/shared/confirm-dialog/confirm.dialog.component';
import { TranslationService } from '@sdbn/common';
import { ResponseMessage } from '@sdbn/appclient';
import { ResponseStatus } from '@sdbn/appclient';
import { FilterAdministrationContext } from '../filter-administration.context';
import { EditorFilter, FilterCondition, EditorFilterTreeNode, LogicalOperator, Filter, PreviewFilter, FilterSortOrder, FilterMailSchedule } from '../filter-administration.model';
import { FilterAdministrationService } from '../filter-administration.service';
import { MasterDetailListEntry } from '@sdbn/common';
import { AbstractWidgetComponent } from '@sdbn/appclient';
import { DictionaryService } from '@sdbn/appclient';
import { DictionaryEntry } from '@sdbn/common';

const source = 'app-filter-administration';

@Component({
  selector: 'app-filter-administration',
  templateUrl: './filter-administration.component.html',
  styleUrls: ['./filter-administration.component.scss']
})
export class FilterAdministrationComponent extends AbstractWidgetComponent implements OnInit, OnDestroy {

  newId = 0;

  filterAdministrationContextSubscription: Subscription;
  languageSubscription: Subscription;
  filterAdministrationContext = new FilterAdministrationContext();

  isLoading = false;

  previewFilters: PreviewFilter[] = [];
  selectedFilter: PreviewFilter;

  responseMessages = new Array<ResponseMessage>();

  constructor(
    public translationService: TranslationService,
    private filterAdministrationService: FilterAdministrationService,
    private dialog: MatDialog,
    private dictionaryService: DictionaryService,
  ) {
    super();
    this.languageSubscription = this.translationService.languageSubject.subscribe(language => {

      this.createTranslatedValuesOnList();

    });
    this.filterAdministrationContextSubscription = this.filterAdministrationContext.contextChange$.subscribe(
      change => {
        try {

        } catch (error) {
          console.error(error);
        }
      }
    );
  }

  ngOnInit(): void {
  }

  ngOnDestroy() {

    super.ngOnDestroy();

    if (this.filterAdministrationContextSubscription != null) {
      this.filterAdministrationContextSubscription.unsubscribe();
    }
    if (this.languageSubscription != null) {
      this.languageSubscription.unsubscribe();
    }
  }

  createTranslatedValuesOnList() {
    for (const supplementaryEquipment of this.previewFilters) {
      supplementaryEquipment.translatedName = this.translationService.translate(supplementaryEquipment.nameDictionaryKey);
    }
  }

  onContextChange(contextChange: ContextChange) {
  }

  onSetElementAndContext() {
    this.filterAdministrationContext.setFilterType(this.widgetReference?.data?.filterType, source);
    this.loadFilters();
  }

  private loadFilters() {
    if (this.filterAdministrationContext.getFilterType() == null) {
      return;
    }

    this.previewFilters = new Array<PreviewFilter>();
    this.filterAdministrationService.getPreviewFilters(this.filterAdministrationContext.getFilterType()).subscribe(
      filters => {
        filters.forEach(
          f => {
            this.previewFilters.push(new PreviewFilter(f));
            this.previewFilters.sort((a, b) => {
              return a.sortOrder > b.sortOrder ? 1 : 0;
            });
          }
        );

        let selected = null;
        if (this.filterAdministrationContext.getFilter()?.id) {
          selected = this.previewFilters.find(pv => pv.id == this.filterAdministrationContext.getFilter()?.id)
        }

        if (selected == null) {
          selected = this.previewFilters[0];
        }

        if (this.previewFilters.length > 0) {
          this.performFilterSelection(selected);
        } else {
          this.addNew();
        }
        this.createTranslatedValuesOnList();
      }
    );
  }

  public setSelectedFilter(filter: PreviewFilter) {
    if (this.filterAdministrationContext.getFilter()?.dirty) {
      const dialogData: ConfirmDialogData = {
        titleKey: 'tmc-yard.widgets.filter-administration.discard.confirm-dialog.title',
        messageKey: 'tmc-yard.widgets.filter-administration.discard.confirm-dialog.message',
        confirm: () => {
          // clear current edit filter
          this.filterAdministrationContext.getFilter().dirty = false;
          this.performFilterSelection(filter);
        },
      };
      this.dialog.open(ConfirmDialogComponent, {
        data: dialogData
      });
    } else {
      this.performFilterSelection(filter);
    }
  }

  private performFilterSelection(filter: PreviewFilter) {
    this.selectedFilter = filter;
    if (this.selectedFilter != null && this.selectedFilter.id >= 0) {
      this.filterAdministrationService.getFilter(this.selectedFilter.id).subscribe(
        result => {
          const editorFilter = new EditorFilter(result);
          // fallback für altdaten
          if (editorFilter.rootNode == null) {
            editorFilter.rootNode = new EditorFilterTreeNode({
              id: this.filterAdministrationContext.getNextTransientId(),
              operator: LogicalOperator.AND,
              conditions: [],
              nodes: []
            });
          }
          this.filterAdministrationContext.setFilter(editorFilter, source);
        }
      );
    }
  }

  public deleteFilter(filter: PreviewFilter) {
    const dialogData: ConfirmDialogData = {
      titleKey: 'tmc-yard.widgets.filter-administration.delete.confirm-dialog.title',
      messageKey: 'tmc-yard.widgets.filter-administration.delete.confirm-dialog.message',
      confirm: () => {
        this.isLoading = true;
        this.filterAdministrationService.deleteFilter(filter.id).subscribe(
          response => {
            this.responseMessages = response.responseMessages;
            if (response.status === ResponseStatus.SUCCESS) {
              this.loadFilters();
            }
            this.isLoading = false;
          },
          error => {
            this.isLoading = false;
          }
        );
      },
    };
    this.dialog.open(ConfirmDialogComponent, {
      data: dialogData
    });
  }

  public addNew() {

    if (this.filterAdministrationContext.getFilter()?.dirty) {
      const dialogData: ConfirmDialogData = {
        titleKey: 'tmc-yard.widgets.filter-administration.discard.confirm-dialog.title',
        messageKey: 'tmc-yard.widgets.filter-administration.discard.confirm-dialog.message',
        confirm: () => {
          this.startFilterCreation();
        },
      };
      this.dialog.open(ConfirmDialogComponent, {
        data: dialogData
      });
    } else {
      this.startFilterCreation();
    }
  }

  private startFilterCreation() {
    const filter = this.createNewFilter();
    this.selectedFilter = new PreviewFilter({ id: filter.id, sortOrder: null, nameDictionaryKey: null });
    this.filterAdministrationContext.setFilter(filter, source);
  }

  private createNewFilter(): EditorFilter {
    const newFilterId = this.filterAdministrationContext.getNextTransientId();

    const rootNode = new EditorFilterTreeNode({
      id: this.filterAdministrationContext.getNextTransientId(),
      filterId: newFilterId,
      conditions: new Array<FilterCondition>(),
      operator: LogicalOperator.AND,
      nodes: new Array<EditorFilterTreeNode>()
    });

    const filter = new EditorFilter({
      name: new DictionaryEntry(),
      id: newFilterId,
      rootNode,
      filterType: this.filterAdministrationContext.getFilterType(),
      sortOrder: this.previewFilters.length + '',
      filterMailSchedules: new Array<FilterMailSchedule>()
    });
    return filter;
  }

  public drop(event) {
    moveItemInArray(this.previewFilters, event.previousIndex, event.currentIndex);
    let index = 0;
    this.previewFilters.forEach(f => f.sortOrder = '' + index++);

    const sortOrders = new Array<FilterSortOrder>();
    for (const p of this.previewFilters) {
      const order = new FilterSortOrder(p);
      sortOrders.push(order);
    }
    this.isLoading = true;
    this.filterAdministrationService.saveSortOrder(sortOrders).subscribe(
      result => {
        this.isLoading = false;
      },
      error => {
        this.isLoading = false;
      }
    );
  }

  public onCancelDetails(dirty: boolean) {

    const dialogData: ConfirmDialogData = {
      titleKey: 'tmc-yard.widgets.filter-administration.cancel.confirm-dialog.title',
      messageKey: 'tmc-yard.widgets.filter-administration.cancel.confirm-dialog.message',
      confirm: () => {
        if (this.selectedFilter.id < 0) {
          if (this.filterAdministrationContext.getFilter()) {
            this.filterAdministrationContext.getFilter().dirty = false;
          }
          this.addNew();
        }
        else {
          this.filterAdministrationService.getFilter(this.selectedFilter.id).subscribe(
            result => {
              const editorFilter = new EditorFilter(result);
              // fallback für altdaten
              if (editorFilter.rootNode == null) {
                editorFilter.rootNode = new EditorFilterTreeNode({
                  id: this.filterAdministrationContext.getNextTransientId(),
                  operator: LogicalOperator.AND,
                  conditions: [],
                  nodes: []
                });
              }
              this.filterAdministrationContext.setFilter(editorFilter, source);
            }
          );
        }
      },
    };
    this.dialog.open(ConfirmDialogComponent, {
      data: dialogData
    });
  }

  public onSaveDetails(filter: EditorFilter) {
    const saveFilter = new Filter(filter);

    if (saveFilter.id > 0) {
      const updatedSortOrder = this.previewFilters.find(p => p.id === saveFilter.id)?.sortOrder;
      if (updatedSortOrder != null) {
        saveFilter.sortOrder = updatedSortOrder;
      }
    }

    this.filterAdministrationService.saveFilter(saveFilter).subscribe(
      () => {
        this.dictionaryService.initializeDataDictionary().subscribe(() => {
          if (this.filterAdministrationContext.getFilter()) {
            this.filterAdministrationContext.getFilter().dirty = false;
          }
          this.loadFilters();
        });
      }
    );
  }

  onSelectionChanged(entry: MasterDetailListEntry) {
    this.setSelectedFilter(entry as PreviewFilter);
  }

  onAddEntry(parentPath: Array<MasterDetailListEntry>) {
    this.addNew();
  }

  onDeleteEntry(entry: MasterDetailListEntry) {
    this.deleteFilter(entry as PreviewFilter);
  }

  getStyle(): WidgetStyle {
    return WidgetStyle.transparent;
  }

}
